import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { companyInfo, projectTypes } from '../mockData';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    projectType: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, projectType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.projectType) {
      toast.error('Please select a project type');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'quote-request',
          ...formData,
        }).toString(),
      });

      if (!response.ok) throw new Error('Form submission failed');

      toast.success('Quote Request Submitted!', {
        description: "We'll get back to you within 24 hours.",
      });

      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        projectType: '',
        description: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send quote request', {
        description: 'Please try again or email us at aliasger.sir@hakimicontrolsystems.com',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20">
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6 font-heading">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to discuss your electrical control panel needs? Fill out the form below and our team will respond promptly.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl font-heading">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-[#FFD617]/20 p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-[#004494]" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Phone</p>
                      <a
                        href={`tel:${companyInfo.phone}`}
                        className="text-gray-600 hover:text-[#004494] transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-[#FFD617]/20 p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-[#004494]" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Email</p>
                      <a
                        href={`mailto:${companyInfo.email}`}
                        className="text-gray-600 hover:text-[#004494] transition-colors break-all"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-[#FFD617]/20 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-[#004494]" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Address</p>
                      <p className="text-gray-600">{companyInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Business Hours */}
              <Card className="border-2 bg-gradient-to-br from-[#004494] to-[#003370] text-white">
                <CardHeader>
                  <CardTitle className="text-2xl font-heading">Business Hours</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span className="font-semibold">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span className="font-semibold">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span className="font-semibold">Closed</span>
                  </div>
                </CardContent>
              </Card>

              {/* Why Contact Us */}
              <Card className="border-2">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 font-heading">
                    Why Choose Us?
                  </h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-[#FFD617] mr-2 text-xl">•</span>
                      <span>24-hour response time</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#FFD617] mr-2 text-xl">•</span>
                      <span>Free consultation and quote</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#FFD617] mr-2 text-xl">•</span>
                      <span>Expert engineering team</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#FFD617] mr-2 text-xl">•</span>
                      <span>Comprehensive support</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Quote Request Form */}
            <Card className="border-2 border-[#004494] shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl font-heading">Request a Quote</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <input type="hidden" name="form-name" value="quote-request" />
                  <p hidden>
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                      className="border-gray-300 focus:border-[#004494] focus:ring-[#004494]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company Name *</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your Company Ltd."
                      required
                      className="border-gray-300 focus:border-[#004494] focus:ring-[#004494]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      required
                      className="border-gray-300 focus:border-[#004494] focus:ring-[#004494]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 XXXXXXXXXX"
                      required
                      className="border-gray-300 focus:border-[#004494] focus:ring-[#004494]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="projectType">Project Type *</Label>
                    <Select onValueChange={handleSelectChange} value={formData.projectType} required>
                      <SelectTrigger className="border-gray-300 focus:border-[#004494] focus:ring-[#004494]">
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Project Description *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project requirements..."
                      rows={5}
                      required
                      className="border-gray-300 focus:border-[#004494] focus:ring-[#004494]"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#004494] hover:bg-[#003370] text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        Send Quote Request
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};